import Cookies from 'js-cookie';
import { mapCurrencyToSymbol } from '../../utils/mapCurrencyToSymbol';
import config from '../../config';
import {
  filterCloseLiveTableGames,
  transformImage,
} from '../../model/algolia/utils';
import { DeviceSettings } from '../../ports-frontend/types';
import {
  AvailableGridTypes,
  CategoriesTypes,
  CategoriesWithContentTypes,
  CategoryBannerType,
  CategoryBannerWithContentType,
  CategoryBaseWithContentType,
  CategoryFinancialWebAttributes,
  CategoryGameCollectionType,
  CategoryGameCollectionWithContentType,
  CategoryGameGridType,
  CategoryGameGridWithContentType,
  CategoryJackpotGridType,
  CategoryJackpotGridWithContentType,
} from '../../types/category/categoryTypes';
import {
  CloseStrategyEnum,
  CloseStrategyType,
  GameCloseStrategies,
  PortalVerticalGameTilePropTypes,
} from '../../types/gameTypes';
import {
  JackpotSlotMapped,
  JackpotsV2StateValues,
  MultipleJackpotV2Interface,
  SingleJackpotV2Interface,
  GamesGlobalJackpotV2Values,
} from '../../types/jackpotTypes';
import {
  CHUNK_SIZE_MAP,
  CONTEXT_ATTRIBUTES,
  GAMING_SEGMENTS_COOKIE_NAME,
  MAXIMUM_BANNER_SIZE_NO_NAVIGATION,
  MAXIMUM_BANNER_SIZE_WITH_NAVIGATION,
  WEB_ATTRIBUTES,
} from './constants';
import {
  CallbackMode,
  LogicContext,
  PartialEventData,
  DeviceOs,
  UserSessionInfo,
  DeviceType,
} from './types';
import { getJackpotPropertiesFromJackpotTile } from '../../utils/jackpotsV2/getJackpotPropertiesFromJackpotTile';
import get from 'src/utils/lodash-alternatives';

export const isIframeWrapperEnabled = (): boolean => {
  return !!(
    config.features['iFrame'] && config.features['iFrame'].mode === 'enabled'
  );
};

export const isModalForMobileEnabled = !!(
  config.features.modalForMobile &&
  config.features.modalForMobile.mode === 'enabled'
);

export const getCallbackMode = (
  currentScreenSize: string,
  deviceInfo: DeviceSettings,
  user?: UserSessionInfo
): CallbackMode =>
  !isIframeWrapperEnabled() &&
  (!!(user && !user.loggedIn) ||
    (currentScreenSize === 'wide' && deviceInfo.deviceType === 'desktop'))
    ? 'event'
    : 'native';

export const trackingDisabled = (context: LogicContext) =>
  !context.features.tracking || context.features.tracking.mode === 'disabled';

export const trackingDelayed = () => {
  return config.features.tracking && config.features.tracking.delayed;
};

export const managePhrases = (
  oldPhrases: string[],
  newPhrase: string | string[],
  length: number
) => {
  let finalPhrases: string[] = [];
  if (typeof newPhrase === 'string') {
    finalPhrases = oldPhrases
      .filter(phrase => phrase !== newPhrase)
      .slice(0, length - 1);
    finalPhrases.unshift(newPhrase);
  } else {
    finalPhrases = oldPhrases;
    const cutted = newPhrase.slice(0, length - 1).reverse();
    cutted.forEach(phrase => {
      if (!finalPhrases.includes(phrase)) {
        finalPhrases.unshift(phrase);
      }
    });
    finalPhrases = finalPhrases.slice(0, length);
  }
  return finalPhrases;
};

export const groupItemsByTypes = (
  items: Array<PortalVerticalGameTilePropTypes>
) => {
  const grouppedItems: {
    [key: string]: Array<PortalVerticalGameTilePropTypes>;
  } = {
    All: items,
  };
  items.forEach(game => {
    if (game.type) {
      if (grouppedItems[game.type]) {
        grouppedItems[game.type].push(game);
      } else {
        grouppedItems[game.type] = [game];
      }
    }
  });

  return grouppedItems;
};

function convertArrayToObject(arr: any[], key: string) {
  return arr.reduce((acc, item) => {
    return {
      ...acc,
      [item[key]]: item,
    };
  }, {});
}

export const getNewGames = (
  items: Array<PortalVerticalGameTilePropTypes>
): { [key: string]: PortalVerticalGameTilePropTypes } => {
  return convertArrayToObject(items, 'id');
};

export const checkIfAllLiveGamesAreClosed = (
  games: PortalVerticalGameTilePropTypes[] = []
) =>
  !!games.every(
    (game: PortalVerticalGameTilePropTypes) =>
      game.table_id && game.table?.online.open === false
  );

const parseTimeToMinutes = (startTime?: string) => {
  if (!startTime) return 0;

  const times = startTime.split(':').map(Number);
  return times[0] * 60 + times[1];
};

export const getStartTimeTables = (
  games: PortalVerticalGameTilePropTypes[] = []
) => {
  const startTimes = games
    .map(game => game.table?.online)
    .filter(tableOnline => tableOnline?.open === false)
    .map(tableOnline => tableOnline?.startTime);

  if (!startTimes) return undefined;
  if (startTimes.length === 1) {
    return startTimes[0];
  } else {
    startTimes.sort((a, b) => parseTimeToMinutes(a) - parseTimeToMinutes(b));
  }

  return startTimes[0];
};

function Banner({
  category,
  windowInnerWidth,
  currentPersonalizedBanners,
}: {
  category: CategoryBannerType;
  windowInnerWidth: number;
  currentPersonalizedBanners?: LogicContext['currentPersonalizedBanners'];
}): CategoryBannerWithContentType {
  return {
    ...category,
    slides: category.bannerSlides.map(slide => ({
      type: 'Banner' as 'Banner',
      gridType: 'Banner',
      banner: {
        asset: slide.banner.asset && {
          ...slide.banner.asset,
          source: transformImage(
            slide.banner.asset.source,
            Math.min(
              isIframeWrapperEnabled()
                ? MAXIMUM_BANNER_SIZE_NO_NAVIGATION
                : MAXIMUM_BANNER_SIZE_WITH_NAVIGATION,
              windowInnerWidth
            )
          ),
        },
        smallLargeAsset: slide.banner.smallLargeAsset && {
          ...slide.banner.smallLargeAsset,
          source: transformImage(
            slide.banner.smallLargeAsset.source,
            Math.min(
              isIframeWrapperEnabled()
                ? MAXIMUM_BANNER_SIZE_NO_NAVIGATION
                : MAXIMUM_BANNER_SIZE_WITH_NAVIGATION,
              windowInnerWidth
            )
          ),
        },
        mediumAsset: slide.banner.mediumAsset && {
          ...slide.banner.mediumAsset,
          source: transformImage(
            slide.banner.mediumAsset.source,
            Math.min(
              isIframeWrapperEnabled()
                ? MAXIMUM_BANNER_SIZE_NO_NAVIGATION
                : MAXIMUM_BANNER_SIZE_WITH_NAVIGATION,
              windowInnerWidth
            )
          ),
        },
        buttonText: slide.banner.buttonText,
        buttonPosition: slide.banner.buttonPosition,
        description: slide.banner.description,
        type: slide.banner.type,
        game: slide.banner.game,
        personalization: slide.banner.personalization && {
          ...slide.banner.personalization,
          rendered:
            currentPersonalizedBanners &&
            currentPersonalizedBanners[slide.banner.personalization.selector],
        },
        route_link: slide.banner.route_link,
        link: slide.banner.link,
        notIndexed: slide.banner.notIndexed,
      },
    })),
  };
}

export const divideGameListToSlides = ({
  gameList = [],
  slideType = 'Grid',
  jackpotSlot,
  unlimited,
}: {
  gameList?: PortalVerticalGameTilePropTypes[];
  slideType: AvailableGridTypes;
  jackpotSlot?: JackpotSlotMapped;
  unlimited?: boolean;
}): CategoryBaseWithContentType['slides'] => {
  let chunkSize = CHUNK_SIZE_MAP[slideType].normal;
  const slides: {
    jackpotSlot?: any;
    gameList: PortalVerticalGameTilePropTypes[];
    type: AvailableGridTypes;
    gamesLimit?: number;
    slideInitialIndex: number;
  }[] = [];
  let slideInitialIndex = 0;

  for (let i = 0, j = gameList.length; i < j; i += chunkSize) {
    chunkSize = CHUNK_SIZE_MAP[slideType].normal;
    switch (slideType) {
      case 'Jackpot Hero':
      case 'Jackpot Banner':
      case 'Widget Hero':
      case 'Widget Banner': {
        slides.push({
          type: slideType,
          gameList: gameList.slice(
            i,
            i === 0 ? CHUNK_SIZE_MAP[slideType].big : i + chunkSize
          ),
          gamesLimit:
            i === 0
              ? CHUNK_SIZE_MAP[slideType].big
              : CHUNK_SIZE_MAP[slideType].normal,
          jackpotSlot,
          slideInitialIndex,
        });
        slideType =
          slideType === 'Widget Banner' || slideType === 'Jackpot Banner'
            ? 'Game Dual'
            : 'Jackpot Nine';
        break;
      }

      case 'Game Banner': {
        if (!gameList[i].bannerImage) {
          const fallbackType = 'Game Dual';
          chunkSize = CHUNK_SIZE_MAP[fallbackType].normal;
          slides.push({
            type: 'Game Dual',
            gameList: gameList.slice(
              i,
              i === 0 ? CHUNK_SIZE_MAP[fallbackType].big : i + chunkSize
            ),
            gamesLimit:
              i === 0
                ? CHUNK_SIZE_MAP[fallbackType].big
                : CHUNK_SIZE_MAP[fallbackType].normal,
            slideInitialIndex,
          });
        } else {
          slides.push({
            type: slideType,
            gameList: gameList.slice(
              i,
              i === 0 ? CHUNK_SIZE_MAP[slideType].big : i + chunkSize
            ),
            gamesLimit:
              i === 0
                ? CHUNK_SIZE_MAP[slideType].big
                : CHUNK_SIZE_MAP[slideType].normal,
            slideInitialIndex,
          });
        }
        break;
      }
      case 'Dynamic Grid':
      case 'Collection': {
        slides.push({
          type: slideType,
          gameList: gameList,
          gamesLimit: gameList.length,
          slideInitialIndex,
        });
        chunkSize = gameList.length;
        break;
      }
      default: {
        slides.push({
          type: slideType,
          gameList: gameList.slice(
            i,
            i === 0 ? CHUNK_SIZE_MAP[slideType].big : i + chunkSize
          ),
          gamesLimit: unlimited
            ? undefined
            : i === 0
            ? CHUNK_SIZE_MAP[slideType].big
            : CHUNK_SIZE_MAP[slideType].normal,
          slideInitialIndex,
        });
        break;
      }
    }
    slideInitialIndex += chunkSize;
  }

  return slides;
};

export function mapGamesWithTables(
  games: PortalVerticalGameTilePropTypes[],
  currentTables: LogicContext['currentTables'],
  locale?: string,
  currency?: string
) {
  if (currentTables && games) {
    return games.map((game: any) => {
      if (game.table_id && currentTables[game.table_id]) {
        return {
          ...game,
          table: {
            ...currentTables[game.table_id],
            locale,
            currencyCode: currency,
          },
        };
      }
      return game;
    });
  }

  return games;
}

const isJackpotV2Valid = (
  jackpotData: Pick<SingleJackpotV2Interface, 'value' | 'currency'>
) =>
  !!jackpotData &&
  jackpotData.value?.state !== JackpotsV2StateValues.WON &&
  !!jackpotData.value?.price &&
  !!jackpotData.currency;

const jackpotToSingle = (
  jackpot: SingleJackpotV2Interface | MultipleJackpotV2Interface,
  provider: string
): SingleJackpotV2Interface | null => {
  if (!jackpot) return null;
  if (isSingleJackpot(jackpot)) {
    return jackpot;
  }

  if (provider === 'Games Global') {
    return {
      code: jackpot.code,
      currency: jackpot.currency,
      timestamp: jackpot.timestamp,
      value: jackpot.values[GamesGlobalJackpotV2Values.MEGA],
    };
  }

  throw Error('Passed jackpot is not supported');
};

export function mapGamesWithJackpotsV2(
  games: PortalVerticalGameTilePropTypes[],
  currentJackpotsV2?: LogicContext['currentJackpotsV2']
) {
  if (!currentJackpotsV2 || !Object.keys(currentJackpotsV2).length || !games) {
    return games;
  }
  return games.map((game: PortalVerticalGameTilePropTypes) => {
    if (!game.jackpotCode || !currentJackpotsV2[game.provider]) {
      return game;
    }

    const jackpot = jackpotToSingle(
      currentJackpotsV2[game.provider][game.jackpotCode],
      game.provider
    );

    if (!jackpot || !isJackpotV2Valid(jackpot)) {
      return game;
    }
    const gameWithJackpot = {
      ...game,
      jackpot: {
        currencySymbol: mapCurrencyToSymbol(jackpot.currency),
        value: jackpot.value.price,
        timestamp: Date.parse(jackpot.timestamp),
        increaseVelocity: game.jackpotVelocity || 0,
        updateInterval: jackpot.value.updateInterval,
      },
    };
    return gameWithJackpot;
  });
}

export const isSingleJackpot = (
  jackpot: SingleJackpotV2Interface | MultipleJackpotV2Interface
): jackpot is SingleJackpotV2Interface => 'value' in jackpot;

export const mergeObjectDepthOne = <T extends Record<string, any>>(
  objectToMerge: T,
  data: T
): T => {
  const merged = Object.entries(data || {}).reduce(
    (mergedValues, [provider, multiJackpot]) => {
      mergedValues[provider as keyof T] = {
        ...objectToMerge?.[provider],
        ...multiJackpot,
      };
      return mergedValues;
    },
    {} as T
  );
  return { ...objectToMerge, ...merged };
};

const prepareCategory = ({
  category,
  multiJackpots,
  currentJackpotsV2,
  currentTables,
  currentPersonalizedBanners,
  windowInnerWidth,
  locale,
  currency,
  gameDetailsInfo,
}: {
  category: CategoriesTypes;
  multiJackpots?: LogicContext['multiJackpots'];
  currentJackpotsV2?: LogicContext['currentJackpotsV2'];
  currentTables?: LogicContext['currentTables'];
  currentPersonalizedBanners?: LogicContext['currentPersonalizedBanners'];
  windowInnerWidth: number;
  locale?: string;
  currency?: string;
  gameDetailsInfo: boolean;
}): CategoriesWithContentTypes => {
  switch (category.component_type) {
    case 'banner':
      return prepareBanner({
        category,
        windowInnerWidth,
        currentPersonalizedBanners,
        gameDetailsInfo,
      });
    case 'game_collection': {
      return prepareGameCollection({
        category,
        currency,
        currentJackpotsV2,
        currentTables,
        windowInnerWidth,
        locale,
        gameDetailsInfo,
      });
    }
    default:
      return prepareGameGrid({
        category,
        currency,
        multiJackpots,
        currentJackpotsV2,
        currentTables,
        windowInnerWidth,
        locale,
        gameDetailsInfo,
      });
  }
};

const prepareBanner = ({
  category,
  currentPersonalizedBanners,
  windowInnerWidth,
  gameDetailsInfo,
}: {
  category: CategoryBannerType;
  currentPersonalizedBanners?: LogicContext['currentPersonalizedBanners'];
  windowInnerWidth: number;
  gameDetailsInfo: boolean;
}): CategoryBannerWithContentType => ({
  ...Banner({ category, windowInnerWidth, currentPersonalizedBanners }),
  gameDetailsInfo,
});

const prepareGameCollection = ({
  category,
  currentJackpotsV2,
  currentTables,
  windowInnerWidth,
  locale,
  currency,
  gameDetailsInfo,
}: {
  category: CategoryGameGridType | CategoryGameCollectionType;
  currentJackpotsV2?: LogicContext['currentJackpotsV2'];
  currentTables?: LogicContext['currentTables'];
  windowInnerWidth: number;
  locale?: string;
  currency?: string;
  gameDetailsInfo: boolean;
}): CategoryGameGridWithContentType | CategoryGameCollectionWithContentType => {
  let { games, overlay, time } = prepareGames({
    games: category.games,
    currentJackpotsV2,
    currentTables,
    locale,
    currency,
    windowInnerWidth,
  });

  const preparedGridCategpry = {
    ...category,
    overlay,
    tableStartTime: time,
    gameDetailsInfo,
    slides: divideGameListToSlides({
      gameList: games,
      slideType: 'Collection',
    }),
  };

  return preparedGridCategpry;
};

const prepareGameGrid = ({
  category,
  multiJackpots,
  currentJackpotsV2,
  currentTables,
  windowInnerWidth,
  locale,
  currency,
  gameDetailsInfo,
}: {
  category:
    | CategoryGameGridType
    | CategoryGameCollectionType
    | CategoryJackpotGridType;
  multiJackpots?: LogicContext['multiJackpots'];
  currentJackpotsV2?: LogicContext['currentJackpotsV2'];
  currentTables?: LogicContext['currentTables'];
  windowInnerWidth: number;
  locale?: string;
  currency?: string;
  gameDetailsInfo: boolean;
}):
  | CategoryGameGridWithContentType
  | CategoryGameCollectionWithContentType
  | CategoryJackpotGridWithContentType => {
  const { games, overlay, time } = prepareGames({
    games: category.games,
    currentJackpotsV2,
    currentTables,
    locale,
    currency,
    windowInnerWidth,
  });

  let jackpotSlot;

  if (
    category.component_type === 'game_grid' ||
    category.component_type === 'jackpot_grid'
  ) {
    jackpotSlot = getJackpotPropertiesFromJackpotTile({
      jackpotTile: category.jackpot_tile,
      multiJackpots: multiJackpots?.[category.jackpot_tile?.provider || ''],
      currency,
      locale,
    });
  }

  const preparedCategory = {
    ...category,
    overlay,
    tableStartTime: time,
    gameDetailsInfo,
    slides:
      !games.length && jackpotSlot
        ? [
            {
              jackpotSlot,
              type: category.type,
              slideInitialIndex: 0,
            },
          ]
        : divideGameListToSlides({
            gameList: games,
            slideType: category.type,
            jackpotSlot,
          }),
  };

  return preparedCategory;
};

export function getCategory({
  category,
  multiJackpots,
  currentJackpotsV2,
  currentTables,
  currentPersonalizedBanners,
  windowInnerWidth = 400,
  locale,
  currency,
}: {
  category: CategoriesTypes;
  multiJackpots?: LogicContext['multiJackpots'];
  currentJackpotsV2?: LogicContext['currentJackpotsV2'];
  currentTables?: LogicContext['currentTables'];
  currentPersonalizedBanners?: LogicContext['currentPersonalizedBanners'];
  windowInnerWidth?: number;
  locale?: string;
  currency?: string;
}): CategoriesWithContentTypes {
  return prepareCategory({
    category,
    currency,
    multiJackpots,
    currentJackpotsV2,
    currentTables,
    currentPersonalizedBanners,
    windowInnerWidth,
    locale,
    gameDetailsInfo: config.features.gameDetails.mode === 'enabled',
  });
}

const prepareGames = ({
  games,
  currentJackpotsV2,
  currentTables,
  locale,
  currency,
  windowInnerWidth,
}: {
  games: PortalVerticalGameTilePropTypes[];
  currentJackpotsV2?: LogicContext['currentJackpotsV2'];
  currentTables?: LogicContext['currentTables'];
  windowInnerWidth: number;
  locale?: string;
  currency?: string;
}) => {
  let gamesWithJackpotV2:
    | PortalVerticalGameTilePropTypes[]
    | null = mapGamesWithJackpotsV2(games, currentJackpotsV2);
  let gamesWithTables:
    | PortalVerticalGameTilePropTypes[]
    | null = mapGamesWithTables(
    gamesWithJackpotV2,
    currentTables,
    locale,
    currency
  );

  let filteredGames: PortalVerticalGameTilePropTypes[] = filterCloseLiveTableGames(
    gamesWithTables
  );

  gamesWithJackpotV2 = null;
  gamesWithTables = null;

  return {
    games: filteredGames,
    overlay: checkIfAllLiveGamesAreClosed(filteredGames),
    time: getStartTimeTables(filteredGames),
  };
};

export function removeQueryParams(url: string) {
  return url.split('?')[0];
}

export function getPreviousPageArray<T extends { pagePath: string }>({
  pageParams,
  previousPageArray = [],
  arraySizeLimit,
}: {
  pageParams: T;
  previousPageArray: T[];
  arraySizeLimit: number;
}): T[] {
  const pageIdArray = previousPageArray.slice(-arraySizeLimit);
  return pageIdArray.slice(-1)[0]?.pagePath === pageParams.pagePath
    ? pageIdArray
    : [...pageIdArray, pageParams];
}

export function getPromotionsPath(promotionSlug?: string): string {
  const basePath = config.base_path || '';
  const promoBasePath = `${basePath}${config.functionalities.promotions.path}`;
  //update promotions with current slug if passed
  return promotionSlug ? `${promoBasePath}/${promotionSlug}` : promoBasePath;
}

export function getProductContext(): string | null {
  return config.features.tracking.productContext;
}

//tmp solution for app specific params

const createUrlParams = (params: any) => {
  const urlParams = new URLSearchParams();
  Object.keys(params).forEach((key: string) => {
    const param = params[key];
    if (param) {
      const mappedKey = WEB_ATTRIBUTES.includes(key) ? `web.${key}` : key;

      if (Array.isArray(param)) {
        urlParams.append(`context[${mappedKey}]`, param.toString());
      } else {
        const paramKey = CONTEXT_ATTRIBUTES.includes(key)
          ? `context[${mappedKey}]`
          : mappedKey;

        urlParams.append(paramKey, param);
      }
    }
  });

  urlParams.sort();
  return urlParams.toString();
};

const mapUserDevice = (deviceType: string, deviceOs: string | null) => {
  if (deviceType === 'desktop') return 'web';
  else {
    if (deviceOs === 'android' || deviceOs === 'ios') return deviceOs;
    return 'mobile_web';
  }
};

const getSegmentsCookie = () =>
  Cookies.getJSON(GAMING_SEGMENTS_COOKIE_NAME)?.segments;

export function mapUrl(
  url: string,
  deviceType: DeviceType,
  deviceOs: DeviceOs,
  categoryAttributes: CategoryFinancialWebAttributes = {}
) {
  const specificParams = createUrlParams({
    ...config.launcher.staticUrlParams,
    agent: mapUserDevice(deviceType, deviceOs),
    segments: getSegmentsCookie(),
    game_sub_category: categoryAttributes.title,
    game_top_category: categoryAttributes.topCategory,
  });

  return `${config.launcher.apiUrl}${url}&${specificParams}&channel=${deviceType}`;
}

export function pathJoin(parts: Array<string | undefined>) {
  const replace = new RegExp('([^:])/{1,}', 'g');
  return parts
    .filter(path => !!path)
    .join('/')
    .replace(replace, '$1/');
}

export function createSearchQuery(params?: {
  [key: string]: string | number | boolean | string[] | undefined;
}): string {
  if (!params) return '';

  const urlParams = new URLSearchParams();
  Object.keys(params).forEach((key: string) => {
    const param = params[key];
    if (param !== undefined) {
      if (Array.isArray(param)) {
        param.forEach((paramItem: string) =>
          urlParams.append(`${key}[]`, paramItem)
        );
        if (param.length === 0) {
          urlParams.append(`${key}[]`, '');
        }
      } else {
        urlParams.append(key, param.toString());
      }
    }
  });

  return `?${urlParams.toString()}`;
}

export function extractNavigationLinksFromNavComponent(
  navigationComponent: {
    properties: {
      categories?: {
        items: {
          routes?: { route_link: string }[];
          hide_on?: Array<'Large' | 'Small'>;
          route_link: string;
        }[];
      }[];
    };
  },
  currentScreenSize: LogicContext['currentScreenSize']
): string[] {
  if (!navigationComponent) return [];

  return navigationComponent.properties.categories
    ? navigationComponent.properties.categories
        .map(category =>
          category.items
            .filter(item =>
              item.hide_on && currentScreenSize
                ? !item.hide_on.includes(
                    currentScreenSize === 'wide' ? 'Large' : 'Small'
                  )
                : true
            )
            .map(item =>
              item.routes?.length
                ? item.routes.map(i => i.route_link).concat(item.route_link)
                : item.route_link
            )
        )
        .flat(3)
    : [];
}

export function omitKeys(
  keys: string[],
  data: PartialEventData
): PartialEventData {
  const filteredObj: PartialEventData = {};
  for (const item of Object.entries(data)) {
    if (!keys.includes(item[0])) {
      filteredObj[item[0]] = item[1];
    }
  }
  return filteredObj;
}

export const getCloseStrategyByParams = (
  integration: string,
  closeStrategies: GameCloseStrategies | CloseStrategyEnum[]
) => get(closeStrategies, integration, CloseStrategyEnum.internal);

export const shouldCloseExternally = (strategy: CloseStrategyType) => {
  return strategy === CloseStrategyEnum.external;
};
